import { parseKraCsv, type ImportedKra } from '@cfl/shared';
import { Layers, Plus, X } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Button,
  Card,
  CardContent,
  Combobox,
  Input,
  Label,
  Progress,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { BulkImportButton, BulkImportDialog } from '@/components/bulk-import-dialog';
import { KraAssignTab } from '@/components/kra/kra-assign-tab';
import { KraSetTab } from '@/components/kra/kra-set-tab';
import { LibraryList } from '@/components/kra/library-list';
import { useKraPermissions } from '@/components/kra/permissions';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import {
  goalLibrary,
  useCreateKraGroup,
  useCreateKrasFromDesignations,
  useDeleteKraGroup,
  useKraGroups,
  useUpdateKraGroup,
  type KraGroup,
} from '@/lib/kra-groups-api';
import { flattenDepartments, useDepartments, useDesignations } from '@/lib/people-api';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

const ORG_WIDE = '__org__';
const NEW = '__new__';

/**
 * Manage KRA.
 *
 * A KRA is a named set of weighted goals - "Software Engineer" holding "fix 10
 * production defects a month" at 30%. It is a stencil: assigning one COPIES its
 * contents, so rewriting a KRA here never alters what an employee was already rated
 * on, which is what makes this page safe to keep tidying mid-cycle.
 */
export function KraGroupsPage() {
  const { canManage } = useKraPermissions();
  // In the URL rather than in state: it makes "add this KRA's goals" a link the group
  // editor can hand you, and a refresh keeps you where you were.
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab = tabParam === 'group' || tabParam === 'assign' ? tabParam : 'add';
  const openKraId = params.get('kra');

  // Reachable at /team/kra without a role gate, because "not an individual
  // contributor" is a fact about the reporting tree that a role ladder cannot express.
  // The API is the control; this is so a stray URL is not a dead end.
  if (!canManage) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-20 text-center">
            <Layers className="mx-auto mb-3 size-6 text-muted-foreground" />
            <p className="text-sm font-medium">Not available to you</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
              KRAs are written by managers, HR and admins. Yours are on{' '}
              <a href="/goals" className="text-primary hover:underline">
                My goals
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/*
        Three tabs, in the order the work happens: write a KRA, weight KRAs into a group,
        point that group at people.

        Assign was moved out once and brought back, and the distinction matters. What was
        wrong was never the tab — it was that the old one read its own employee list from
        a second endpoint whose scoping disagreed with the goal screen's, so a manager saw
        all 71 people. That endpoint is gone; this tab reads the same scoped list Goal
        assignments does. Finishing a group and assigning it is one job, and sending
        somebody to another screen to finish it was the worse trade.

        Add KRA and Create Group stay separate because they edit different things at
        different levels, and one of them is SHARED. A KRA ("Software Engineer") is
        org-wide and appears in many groups; a group ("Tech Lead") is one job's weighting
        of them. Folding the KRA editor into a group row would mean retitling or
        re-weighting a KRA from inside one group silently changed it for every other group
        using it — and each group holds ~10 KRAs of ~5 goals each, so it would also nest a
        full editor inside ten rows of another one.

        The hand-off between them is a link, not a hunt: creating a KRA from the group
        editor left an empty stencil and a toast saying to go and fill it in somewhere
        else. The tab is in the URL now, so that is one click and lands on the right KRA.
      */}
      <Tabs value={tab} onValueChange={(value) => setParams({ tab: value }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="add">Add KRA</TabsTrigger>
          <TabsTrigger value="group" data-guide="kra-group-tab">Create Group</TabsTrigger>
          <TabsTrigger value="assign">Assign KRA</TabsTrigger>
        </TabsList>

        <TabsContent value="add" className="mt-6 space-y-6">
          {/* Keyed so arriving from a "fill this one in" link remounts on the KRA it
              names, rather than keeping whatever was open before. */}
          <AddKraTab key={openKraId ?? 'none'} initialKraId={openKraId} />
        </TabsContent>
        <TabsContent value="group" className="mt-6">
          <KraSetTab />
        </TabsContent>
        <TabsContent value="assign" className="mt-6">
          <KraAssignTab />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function AddKraTab({ initialKraId }: { initialKraId: string | null }) {
  const { data, isPending, error } = useKraGroups();
  const { data: designations } = useDesignations();
  // Opened straight from the group editor's "this KRA has no goals yet" link, so the
  // hand-off between the two tabs lands on the KRA it is talking about.
  const [selectedId, setSelectedId] = React.useState<string | null>(initialKraId);
  /** Pre-fills the name when a new KRA is started from the search box. */
  const [draftName, setDraftName] = React.useState('');
  const [importing, setImporting] = React.useState(false);
  const remove = useDeleteKraGroup();
  const perms = useKraPermissions();

  function startNew(name: string) {
    setDraftName(name);
    setSelectedId(NEW);
  }

  const kras = data ?? [];

  /**
   * Designations with no KRA yet, offered inside the search box rather than printed
   * above it. They are a prompt, not a queue: a KRA is worth nothing until it has
   * goals, so creating 41 empty ones up front would fill the library with entries
   * that rate nothing, and HR would then have to delete the ones they never wanted.
   */
  const uncovered = React.useMemo(() => {
    const covered = new Set(kras.map((k) => k.name.trim().toLowerCase()));
    return (designations ?? [])
      .filter((d) => !covered.has(d.title.trim().toLowerCase()))
      // Job titles people actually hold first: a KRA for a designation 8 people share
      // matters more than one for a vacancy.
      .sort((a, b) => b.employeeCount - a.employeeCount || a.title.localeCompare(b.title));
  }, [kras, designations]);

  const selected = selectedId === NEW ? null : (kras.find((k) => k.id === selectedId) ?? null);
  const editorOpen = selectedId !== null && (selectedId === NEW || selected !== null);

  const totalGoals = kras.reduce((sum, k) => sum + k.items.length, 0);
  const scoped = kras.filter((k) => k.departmentId !== null).length;

  /**
   * Held between the bin icon and the confirmation.
   *
   * A styled dialog rather than `window.confirm`, which cannot be read by anyone using
   * the app in a language the browser chrome does not match, cannot say what is safe in
   * two sentences, and looks like a phishing prompt.
   */
  const [deleting, setDeleting] = React.useState<{ id: string; name: string } | null>(null);

  async function onDelete(item: { id: string; name: string }) {
    try {
      await remove.mutateAsync(item.id);
      if (selectedId === item.id) setSelectedId(null);
      toast.success(`Deleted "${item.name}"`);
    } catch (e) {
      toast.error('Could not delete that KRA', { description: err(e) });
    }
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          label="KRAs"
          value={isPending ? '—' : kras.length}
          caption={kras.length === 0 ? 'none defined yet' : 'reusable sets of goals'}
        />
        <StatTile
          label="Goals in the library"
          value={isPending ? '—' : totalGoals}
          caption="copied onto a goal set when assigned"
        />
        <StatTile
          label="Department-scoped"
          value={isPending ? '—' : scoped}
          caption={`${kras.length - scoped} available org-wide`}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <LibraryList
          items={kras.map((kra) => ({
            id: kra.id,
            name: kra.name,
            subtitle: `${kra.items.length} goal${kra.items.length === 1 ? '' : 's'} · ${kra.departmentName ?? 'Org-wide'}`,
            canModify: perms.canModify(kra),
            ownerNote: perms.ownerNote(kra),
          }))}
          isPending={isPending}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDelete={(item) => setDeleting(item)}
          searchPlaceholder="Find or create a KRA"
          emptyHint="No KRAs yet. Type a name to create one, or bulk import a spreadsheet."
          suggestions={uncovered.map((d) => ({
            id: d.id,
            name: d.title,
            subtitle:
              d.employeeCount > 0
                ? `Designation · ${d.employeeCount} ${d.employeeCount === 1 ? 'person' : 'people'} · no KRA yet`
                : 'Designation · no KRA yet',
          }))}
          onCreate={startNew}
          createLabel="Create KRA"
          bulkDelete={{
            noun: 'KRAs',
            description:
              'KRAs already assigned from these are unaffected — assigning copies. Any KRA a group still uses is refused and named, so delete or edit those groups first.',
            run: (id) => remove.mutateAsync(id),
          }}
        >
          <Button hint={"Create a new KRA"} variant="outline" className="gap-2" onClick={() => startNew('')}>
            <Plus className="size-4" strokeWidth={2.4} />
            Create New KRA
          </Button>
          <BulkImportButton onClick={() => setImporting(true)}>Bulk import</BulkImportButton>
          {uncovered.length > 0 && <CreateAllDesignations count={uncovered.length} />}
        </LibraryList>

        {editorOpen ? (
          // Remounting on selection change is what keeps the draft honest: no effect
          // syncing props into state, so a background refetch cannot wipe unsaved edits.
          <KraEditor
            key={selectedId === NEW ? `new:${draftName}` : selectedId}
            kra={selected}
            initialName={draftName}
            library={kras}
            onClose={() => setSelectedId(null)}
            onCreated={(id) => setSelectedId(id)}
          />
        ) : (
          <Card>
            <CardContent className="py-20 text-center">
              <Layers className="mx-auto mb-3 size-6 text-muted-foreground" />
              <p className="text-sm font-medium">Pick a KRA to view or edit</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                A KRA is a named set of weighted goals a manager can drop onto an employee in one
                action, instead of retyping the same five for everyone.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <KraImportDialog open={importing} onOpenChange={setImporting} />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title={`Delete "${deleting?.name ?? ''}"?`}
        // The one thing HR needs to know: nobody's rated KRAs move, because assigning
        // copies. A KRA still used by a group is refused by the API, which names them.
        description="KRAs already assigned from it are unaffected — assigning copies, so nothing anybody has been rated on changes. If a group still uses this KRA the delete is refused and you will be told which groups to fix first."
        confirmLabel="Delete KRA"
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          const item = deleting;
          setDeleting(null);
          if (item) void onDelete(item);
        }}
      />
    </>
  );
}

/**
 * The bulk escape hatch for the designations that have no KRA yet.
 *
 * One line under the list, not a wall of 41 chips: the chips were a second copy of
 * Organization → Designations printed into this page, and they pushed the library and
 * the editor a screen down. Searching reaches any one of them; this is here only for
 * the day HR wants the lot in one action.
 */
function CreateAllDesignations({ count }: { count: number }) {
  const createAll = useCreateKrasFromDesignations();

  async function onCreateAll() {
    try {
      const result = await createAll.mutateAsync();
      toast.success(`${result.created} KRA(s) created`, {
        description:
          result.skipped > 0
            ? `${result.skipped} designation(s) already had one and were left untouched. Add goals to each new KRA.`
            : 'Now add the goals each one is measured on.',
      });
    } catch (e) {
      toast.error('Could not create those KRAs', { description: err(e) });
    }
  }

  return (
    <p className="px-1 text-xs text-muted-foreground">
      {count} designation{count === 1 ? '' : 's'} with no KRA — search above to add one, or{' '}
      <button
        type="button"
        className="font-medium text-primary hover:underline disabled:opacity-50"
        disabled={createAll.isPending}
        onClick={() => void onCreateAll()}
      >
        {createAll.isPending ? 'creating…' : `create all ${count}`}
      </button>
      .
    </p>
  );
}

interface DraftGoal {
  name: string;
  description: string;
  weightage: number;
}

const BLANK_GOAL: DraftGoal = { name: '', description: '', weightage: 0 };

function KraEditor({
  kra,
  initialName,
  library,
  onClose,
  onCreated,
}: {
  kra: KraGroup | null;
  /** Pre-filled name for a new KRA started from a designation. Ignored when editing. */
  initialName: string;
  library: KraGroup[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = React.useState(kra?.name ?? initialName);
  const [description, setDescription] = React.useState(kra?.description ?? '');
  const [departmentId, setDepartmentId] = React.useState(kra?.departmentId ?? ORG_WIDE);
  const [goals, setGoals] = React.useState<DraftGoal[]>(
    kra?.items.length
      ? kra.items.map((i) => ({
          name: i.name,
          description: i.description ?? '',
          weightage: i.weightage,
        }))
      : [BLANK_GOAL],
  );

  const { data: departmentTree } = useDepartments();
  const departments = React.useMemo(
    () => flattenDepartments(departmentTree ?? []),
    [departmentTree],
  );
  const existingGoals = React.useMemo(() => goalLibrary(library), [library]);

  const create = useCreateKraGroup();
  const update = useUpdateKraGroup();
  const saving = create.isPending || update.isPending;
  // Anyone may read a KRA; only its author or HR may save a change to it.
  const perms = useKraPermissions();
  const canEdit = kra === null ? perms.canManage : perms.canModify(kra);

  // Same rule as the group editor: never disable Save without saying why.
  const blocker: string | null = !canEdit
    ? `${kra?.createdByName ?? 'HR'} created this KRA — ask them or HR to change it.`
    : name.trim() === ''
      ? 'Give the KRA a name.'
      : goals.every((g) => g.name.trim() === '')
        ? 'Add at least one goal.'
        : null;

  const total = goals.reduce((sum, g) => sum + (Number.isFinite(g.weightage) ? g.weightage : 0), 0);

  function setGoal(index: number, patch: Partial<DraftGoal>) {
    setGoals((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  }

  /** Append a goal, consuming the blank starter row rather than leaving it stranded. */
  function addGoal(goal: DraftGoal) {
    setGoals((prev) => [
      ...prev.filter((g) => g.name.trim() !== '' || g.description.trim() !== ''),
      goal,
    ]);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    const cleaned = goals
      .map((g) => ({
        name: g.name.trim(),
        description: g.description.trim() || null,
        weightage: g.weightage,
      }))
      .filter((g) => g.name.length > 0);

    if (cleaned.length === 0) {
      toast.error('A KRA needs at least one goal');
      return;
    }

    const body = {
      name: name.trim(),
      description: description.trim() || null,
      departmentId: departmentId === ORG_WIDE ? null : departmentId,
      items: cleaned,
    };

    try {
      if (kra) {
        await update.mutateAsync({ id: kra.id, ...body });
        toast.success(`Updated "${body.name}"`);
      } else {
        const created = await create.mutateAsync(body);
        toast.success(`Created "${body.name}"`);
        onCreated(created.id);
      }
    } catch (e) {
      toast.error('Could not save that KRA', { description: err(e) });
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="kraName">KRA name</Label>
              {/* Plain text. The designation suggestions moved to the search box that
                  opens this editor, so by the time anyone is here the name is either
                  already filled from a designation or being written from scratch — a
                  second picker on the same value was one place too many to change it. */}
              <Input
                id="kraName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Software Engineer"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="kraDepartment">Available to</Label>
              <Combobox
                id="kraDepartment"
                value={departmentId}
                onChange={setDepartmentId}
                options={[
                  { value: ORG_WIDE, label: 'Everyone' },
                  ...departments.map((d) => ({ value: d.id, label: d.label })),
                ]}
                searchPlaceholder="Search departments"
                emptyText="No department matches"
              />
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="kraDescription">Description</Label>
              <Input
                id="kraDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this set is for"
              />
            </div>
          </div>

          <div className="space-y-4 border-t pt-5">
            <div>
              <Label>Goals</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Weightage splits this KRA&apos;s own share between its goals — optional, and
                carried onto the employee&apos;s goal when the KRA is assigned. Leave them all at 0
                and the goals count equally.
              </p>
            </div>

            {goals.map((goal, index) => (
              <div key={index} className="rounded-md border p-4">
                <div className="mb-3 flex items-start gap-2">
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor={`goalName${index}`} className="text-xs">
                      Goal name
                    </Label>
                    <Input
                      id={`goalName${index}`}
                      value={goal.name}
                      onChange={(e) => setGoal(index, { name: e.target.value })}
                      placeholder="Fix 10 production defects a month"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mt-6"
                    aria-label={`Remove goal ${index + 1}`}
                    disabled={goals.length === 1}
                    onClick={() => setGoals((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <X className="size-4" />
                  </Button>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor={`goalDescription${index}`} className="text-xs">
                    Description
                  </Label>
                  <Textarea
                    id={`goalDescription${index}`}
                    rows={2}
                    value={goal.description}
                    onChange={(e) => setGoal(index, { description: e.target.value })}
                    placeholder="What good looks like, and how it is measured"
                  />
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <Label htmlFor={`goalWeightage${index}`} className="text-xs">
                    Weightage
                  </Label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    className="h-1.5 flex-1 accent-primary"
                    aria-label={`Weightage for goal ${index + 1}`}
                    value={goal.weightage}
                    onChange={(e) => setGoal(index, { weightage: Number(e.target.value) })}
                  />
                  <Input
                    id={`goalWeightage${index}`}
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    className="w-20"
                    value={goal.weightage}
                    // Whole numbers only, so 33.33 x 3 can never total 99.99 downstream.
                    onChange={(e) =>
                      setGoal(index, { weightage: Math.round(Number(e.target.value) || 0) })
                    }
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              </div>
            ))}

            {/* One control, not two. "Add an existing goal" and "Add Goal" were the
                same action with the same result — a row appended below — split across
                a dropdown and a button, so reusing a goal meant knowing which of the
                two to reach for. Searching finds it; typing makes it. */}
            <Combobox
              // Nothing stays selected: the pick lands as a row underneath.
              value={null}
              className="sm:w-96"
              placeholder="Add a goal — search existing, or type a new one"
              searchPlaceholder="Search goals across every KRA"
              emptyText="No goal matches"
              options={existingGoals.map((g) => ({
                value: g.id,
                label: g.name,
                hint: `${g.weightage}%`,
              }))}
              onChange={(value) => {
                const picked = existingGoals.find((g) => g.id === value);
                if (picked) {
                  addGoal({
                    name: picked.name,
                    description: picked.description ?? '',
                    weightage: picked.weightage,
                  });
                }
              }}
              onCreate={(name) => addGoal({ ...BLANK_GOAL, name })}
              createLabel="Add new goal"
            />

            {/* A hint, never a gate. The exact-100 rule belongs to the KRA *group* one
                level up, where the 100% that decides the appraisal is actually spent;
                these are relative shares inside one KRA, so 60/40 and 6/4 are the same
                split and neither is wrong. Blocking Save on 100 here would also refuse
                the half-written stencil the schema deliberately allows. */}
            <div className="space-y-1.5">
              <Progress value={Math.min(total, 100)} />
              <p className="text-xs text-muted-foreground">
                {total === 0 ? (
                  <>
                    No split set — these {goals.length} goal{goals.length === 1 ? '' : 's'} count
                    equally
                  </>
                ) : (
                  <>
                    {total}% across {goals.length} goal{goals.length === 1 ? '' : 's'} — relative
                    shares, they need not total 100
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
            {blocker && <p className="mr-auto text-xs text-muted-foreground">{blocker}</p>}
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || blocker !== null}>
              {saving ? 'Saving…' : kra ? 'Save changes' : 'Create KRA'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Bulk import KRAs — one row per goal, repeating the KRA name.
 *
 * The shell (preview, per-line errors, "creates only") is shared with the group
 * import; all that differs is what a row means. Nothing is blocked here: a KRA whose
 * goals do not total 100 is legitimate, because that budget is spent one level up in
 * the group. A name the library already holds is refused by the API and comes back as
 * skipped, by name.
 */
function KraImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateKraGroup();

  return (
    <BulkImportDialog<ImportedKra>
      open={open}
      onOpenChange={onOpenChange}
      title="Bulk import KRAs"
      description="One row per goal, repeating the KRA name — three goals under one KRA is three rows. Import only creates; a KRA that already exists is left exactly as it is."
      templateHref="/kra-import-sample.csv"
      noun="KRA"
      parse={(csv) => {
        const result = parseKraCsv(csv);
        return { rows: result.kras, errors: result.errors };
      }}
      summarise={(kra) => ({
        text: `${kra.goals.length} goal${kra.goals.length === 1 ? '' : 's'} · ${kra.goals.reduce(
          (sum, g) => sum + g.weightage,
          0,
        )}%`,
      })}
      create={(kra) =>
        create.mutateAsync({
          name: kra.name,
          items: kra.goals.map((g) => ({
            name: g.name,
            description: g.description,
            weightage: g.weightage,
          })),
        })
      }
    />
  );
}
